import { createElement } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { resolveLinkAttributes } from '@source-components-react/shared/links';
import { renderIconChildren } from '@source-components-react/shared/icons';
import type { IconNode, WidgetInstanceProps } from '@source-components-react/types';
import { resolveIconAttributeValue } from '../r-icon/icon-attribute-value';
import {
  applyButtonColumnStyles,
  buildButtonRenderStyles,
  disabledStyles,
} from './button-preset-styles';
import { resolveButtonClickHandler } from './button-instance-props';
import { resolveButtonPreset } from './resolve-button-preset';
import {
  BUTTON_ICON_ATTRIBUTE,
  BUTTON_LINK_ATTRIBUTE,
  BUTTON_PRESET_ATTRIBUTE,
  resolveButtonAriaLabel,
  resolveButtonDisabled,
  resolveButtonLabel,
  resolveIconPosition,
} from './button-attributes';

/**
 * One interactive element — `<a>` when the button links somewhere, otherwise
 * `<button>` — and never a wrapper around it.
 *
 * Appearance is a preset by default and column styles on top: the preset
 * styles are spread first so anything the author set in Column Styles lands
 * later in the object and wins. Colour properties the library stylesheet
 * owns (`background-color`, `color`, `border-color`) travel as custom
 * properties instead, so `:hover` can change them. A column background
 * rewrite remaps onto those slots. That is why this component declares no
 * style attributes of its own.
 *
 * Nothing here needs the browser. The element, its label and any icon
 * geometry are all in the server's HTML, with no resolution after hydration.
 * A host's `onClick` is the one exception, and it can only be: an event
 * listener does not exist until the widget hydrates, which is why the disabled
 * state is still expressed in markup rather than by cancelling clicks.
 */
export function RButton({
  componentAttributes,
  styleProps,
  className,
  widgetInstanceProps,
  columnPath,
}: Readonly<{
  componentAttributes?: { [key: string]: any };
  /** Column styles from the widget pipeline. Always applied to the button itself. */
  styleProps?: { [key: string]: any };
  /** Host-app classes for this column. The single root element carries them. */
  className?: string;
  /**
   * Props the embedding app addressed at this instance. `onClick` is the only
   * key read; see `button-instance-props.ts`.
   */
  widgetInstanceProps?: WidgetInstanceProps;
  /**
   * This instance's column path, emitted as `data-rulecms-column-path` so a
   * host's `RuleCMSButtonClicks` can address the button in server-rendered
   * HTML, where no handler can be attached by props.
   */
  columnPath?: string;
}>) {
  const preset = resolveButtonPreset(componentAttributes?.[BUTTON_PRESET_ATTRIBUTE]);
  const disabled = resolveButtonDisabled(componentAttributes);
  const onClick = resolveButtonClickHandler({ widgetInstanceProps, disabled });

  const style: CSSProperties = {
    ...buildButtonRenderStyles(preset),
    ...(disabled ? disabledStyles : {}),
    ...applyButtonColumnStyles(styleProps),
  };

  const content = renderContent(componentAttributes);
  const ariaLabel = resolveButtonAriaLabel(componentAttributes);
  const link = resolveLinkAttributes(componentAttributes?.[BUTTON_LINK_ATTRIBUTE]);

  const sharedProps = {
    style,
    'data-rulecms-button': '',
    'data-rulecms-button-variant': preset.variant,
    'data-rulecms-button-tone': preset.tone,
    ...(columnPath !== undefined ? { 'data-rulecms-column-path': columnPath } : {}),
    ...(className !== undefined ? { className } : {}),
    ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}),
    ...(onClick !== undefined ? { onClick } : {}),
  };

  if (link) {
    // A disabled link keeps its role and its look but loses its href, which is
    // what makes it unreachable by pointer and by keyboard. The alternative —
    // an href plus a click handler that cancels it — would need JavaScript on
    // the page to be correct, and a widget must be right before it hydrates.
    if (disabled) {
      return (
        <a {...sharedProps} role="link" aria-disabled="true">
          {content}
        </a>
      );
    }
    // A host handler on a link runs and then the browser still navigates. The
    // link is what the author configured and what the markup promises, so
    // swallowing it here would make the anchor lie about where it goes; a host
    // that wants the click instead of the navigation calls preventDefault.
    return (
      <a {...link} {...sharedProps}>
        {content}
      </a>
    );
  }

  // `type="button"` is not a default worth losing: an untyped button dropped
  // inside a host page's existing <form> submits it on click.
  return (
    <button type="button" disabled={disabled} {...sharedProps}>
      {content}
    </button>
  );
}

const renderContent = (componentAttributes?: { [key: string]: any }): ReactNode => {
  const label = resolveButtonLabel(componentAttributes);
  const iconNode = resolveIconAttributeValue(componentAttributes?.[BUTTON_ICON_ATTRIBUTE]);

  if (!iconNode) {
    return label;
  }

  const icon = renderButtonIcon(iconNode);
  if (resolveIconPosition(componentAttributes) === 'trailing') {
    return (
      <>
        {label}
        {icon}
      </>
    );
  }
  return (
    <>
      {icon}
      {label}
    </>
  );
};

/**
 * Sized in `em` so it tracks the button's font size across every preset and
 * any font size the author sets, and hidden from assistive technology because
 * the control is already named by its label or `aria-label`.
 */
const renderButtonIcon = (iconNode: IconNode) =>
  createElement(
    'svg',
    {
      xmlns: 'http://www.w3.org/2000/svg',
      width: '1em',
      height: '1em',
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      style: { flexShrink: 0 },
      'aria-hidden': true,
    },
    ...renderIconChildren(iconNode)
  );
